// components/tracker/drug-colors.ts
//
// A drug's colour is its sphere's colour (FE-DRUG-AVATAR-1, Phase 3a). The
// procedural avatar in `lib/drug-avatars/` paints from a four-stop ramp chosen
// by the seed, and `avatarAccentColor(seed)` returns three of those stops
// without rendering anything. Every field below is one of those stops or a
// tint of one, so a chip, a monogram gradient and the sphere beside them
// always agree, on every surface, with nothing to keep in step by hand.
//
// The ten-slot palette assigned by portfolio order, and its SLOT <-> AVATAR
// invariant against `/images/drug-avatars/01..10.jpg`, retired here. Position
// is no longer an identity anywhere.
//
// WHERE THE IDENTITY RULE LIVES. `lib/drug-avatars/avatar-cache.ts` holds it:
// `accentForDrug(assetId, name)` seeds by the canonical asset ID when one
// resolves and by the normalised name only as the marked fallback. A component
// under a route's `DrugIdentityProvider` calls `useDrugColor(name)` from
// `drug-identity-context.tsx`; a site without a provider (the public company
// page) calls `accentForDrug` with the id from its own payload. Both feed the
// accent through `drugColorFromAccent` below, the one mapping onto the chip
// shape. The name-only bridge `drugColor(name, portfolio)` that carried the
// un-swapped sites through 3a and 3b is gone (Phase 4): nothing may colour a
// drug without first asking for its asset.
//
// Guardrails: the reserved hues stay reserved. The ramp holds no alarm-red
// (#B3261E) and no alert-green (#1E8E3E); `scripts/test-drug-colors.ts` pins
// that. Colour never carries meaning alone: the drug name is always printed
// beside it.

import type { AvatarAccentColor } from "@/lib/drug-avatars/avatar-recipe"

export type DrugColor = {
  /**
   * A label for whoever reads a value in a debugger: the fill's hex. NOTHING
   * READS IT (verified by grep across `components/`, `lib/`, `app/` and
   * `scripts/` on 2026-08-23 and again on 2026-09-06). If this ever becomes a
   * key, it stops being a comment.
   */
  name: string
  /** Deep shade: chip text + monogram initial. The ramp's darkest stop. */
  base: string
  /** 10% tint behind the chip text, cut from `fill`. */
  chipBg: string
  /** Soft duotone for the card monogram (135deg, light -> mid). */
  gradFrom: string
  gradTo: string
  /**
   * Solid fill for a SELECTED chip that sits next to the drug's sphere avatar
   * (the Changes sheet's drug row). The ramp's mid stop, which is the sphere's
   * dominant hue, so the chip and the sphere read as one object.
   */
  fill: string
  /** Type colour on `fill`. White for every ramp. */
  chipInk: string
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "")
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

/**
 * Map the sphere's accent triple onto the chip/monogram shape every colour
 * call site reads. One mapping, used by `useDrugColor` in the identity
 * context and by the provider-less public sites, so the two can never
 * disagree.
 */
export function drugColorFromAccent(accent: AvatarAccentColor): DrugColor {
  const [r, g, b] = hexToRgb(accent.fill)
  return {
    name: accent.fill,
    base: accent.ink,
    chipBg: `rgba(${r}, ${g}, ${b}, 0.10)`,
    gradFrom: accent.soft,
    gradTo: accent.fill,
    fill: accent.fill,
    chipInk: "#FFFFFF",
  }
}
